import { useState, createContext, useMemo } from "react";
import genericStyles from "../../Styles/genericStyles.module.css";
import MUIDataTableCustomStyle from "../../Components/MUIDataTableCustomStyle";
import { Vocabulary } from "../../Utils/Vocabulary";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import { ClientModel } from "../../Utils/Models";
import { Button, Chip } from "@mui/material";
import { Address, Filter } from "../../Utils/Types";
import ImportExcelModal from "../ImportExcelModal";
import { ClientExcelHeader, FormsIds, TablesIds } from "../../Utils/Constants";
import { postData } from "../../Services/postData";
import { EComponentTypes, ETableEditAction } from "../../Utils/Enums";
import { checkIfStringContainsOnlyOneChar } from "../../Utils/Utils";
import { AxiosResponse } from "axios";
import Filters from "../Widgets/Filters";
import * as yup from "yup";
import useCustomMemo from "../../Hooks/useCustomMemo";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import GenericModal from "../GenericModal";
import ClientForm from "./ClientForm";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import withRole from "../../Hooks/withRole";

export const CRUDContext = createContext(new ClientModel());

function ClientsTable(props: any) {
  const [contextValue, setContextValue] = useState(new ClientModel());
  const [openImportModal, setOpenImportModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [triggerReload, setTriggerReload] = useState<undefined | boolean>();
  const memo = useCustomMemo();
  const clientCategories = memo?.storage.clientCategories || [];
  const filters = useMemo<Filter[]>(() => [
    {
      property: "category",
      label: Vocabulary.category,
      type: EComponentTypes.AUTOCOMPLETE,
      validation: yup.mixed().nullable(),
      defaultValue: null,
      options: clientCategories,
      autocompleteFilterProperty: "_id",
    },
    {
      property: "contactDate",
      label: Vocabulary.contactDate,
      type: EComponentTypes.DATE_PICKER,
      validation: yup.string().required(Vocabulary.requiredField),
      defaultValue: null,
    },
  ], [clientCategories]);
  const clientsColumns = [
    {
      label: `${Vocabulary?.firstName}`,
      name: "firstName",
      options: {
        sort: false,
      },
    },
    {
      label: `${Vocabulary?.lastName}`,
      name: "lastName",
      options: {
        sort: false,
      },
    },
    {
      label: `${Vocabulary.phone}`,
      name: "phone",
      options: {
        setCellHeaderProps: () => ({
          align: "center",
        }),
        setCellProps: () => ({
          align: "center",
          minHeight: 100,
        }),
        filter: false,
        sort: false,
        empty: true,
        customBodyRender: (phone: string) => {
          return (
            <>
              {phone ? (
                <a
                  key={phone}
                  href={`tel:${phone}`}
                  style={{ textDecoration: "none" }}
                >
                  <Chip
                    icon={<LocalPhoneIcon fontSize="small" />}
                    color="primary"
                    label={phone}
                    style={{ padding: 5 }}
                  />
                </a>
              ) : null}
            </>
          );
        },
      },
    },
    {
      label: `${Vocabulary.email}`,
      name: "email",
      options: {
        sort: false,
      },
    },
    {
      label: `${Vocabulary.address}`,
      name: "address",
      options: {
        setCellHeaderProps: () => ({
          align: "center",
        }),
        setCellProps: () => ({
          align: "center",
        }),
        filter: false,
        sort: false,

        empty: true,
        customBodyRender: (address: Address) => {
          return <div>{address?.city}</div>;
        },
      },
    },
  ];

  /**
   *
   * @param data
   */
  const importClients = (data: any) => {
    postData(endpoints.client.import, data).then((res: AxiosResponse) => {
      if (res) {
        setOpenImportModal(false);
        setTriggerReload(triggerReload !== undefined ? !triggerReload : false);
      }
    });
  };

  /**
   *
   * @param row
   * @returns
   */
  function getNewClientRow(row: any) {
    return {
      name: row.CLIENT,
      category: row.CATEGORIE,
      periodicity: row.SAPTAMANA,
      email: row.EMAIL,
      fiscalCode: row.COD_FISCAL,
      tradeRegisterNo: checkIfStringContainsOnlyOneChar(
        row.NR_REGISTRU_COMERT,
        "-"
      ),
      observations: row.OBSERVATII,
      addressDetails: row.ADRESA,
      city: row.ORAS,
      county: row.JUDET,
      country: row.TARA,
      bank: row.BANCA,
      iban: row.CONT_BANCAR,
      contactPerson: row.PERSOANA_DE_CONTACT,
      phone: row.TELEFON,
    };
  }

  return (
    <div className={genericStyles.mainContainer}>
      <Filters filters={filters} customFilterModel={new ClientModel()} />
      <CRUDContext.Provider value={contextValue}>
        <MUIDataTableCustomStyle
          onAddAction={ETableEditAction.REDIRECT}
          onEditAction={ETableEditAction.MODAL}
          rowClickURL={`${localUrlEnum.newOrder}/`}
          title={Vocabulary.clients}
          columns={clientsColumns}
          shouldTriggerStorageUpdate={true}
          CRUDEndPoint={endpoints.client.main}
          id={TablesIds.clients}
          setContextValue={setContextValue}
          triggerReload={triggerReload}
          handleModalForm={() => {
            setOpenEditModal(true);
          }}
          hideDeleteButton={!props?.showDeleteButton}
          addOrEditForm={
            <GenericModal
              open={openEditModal}
              onClose={() => setOpenEditModal(false)}
              title={Vocabulary.editClient}
              actions={
                <div className={genericStyles.editClientModalEditButton}>
                  <Button
                    color="error"
                    variant="contained"
                    onClick={() => setOpenEditModal(false)}
                    startIcon={<CancelIcon />}
                  >
                    {Vocabulary.cancel}
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    form={FormsIds.clients}
                    color="success"
                    startIcon={<SaveIcon />}
                  >
                    {Vocabulary.save}
                  </Button>
                </div>
              }
            >
              <ClientForm
                selectedClient={contextValue}
                onClose={() => setOpenEditModal(false)}
                onSuccess={() => setTriggerReload(!triggerReload)}
              />
            </GenericModal>
          }
        ></MUIDataTableCustomStyle>

        <ImportExcelModal
          headers={ClientExcelHeader}
          title={`${Vocabulary.import} ${Vocabulary.clients}`}
          openImportModal={openImportModal}
          setOpenImportModal={setOpenImportModal}
          getNewRow={getNewClientRow}
          getRowsAsArrayData={(data: any) => importClients(data)}
        />
      </CRUDContext.Provider>
    </div>
  );
}

export default withRole(ClientsTable);
